import { gql, useQuery } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import { useParams } from 'react-router-dom';

import { NuxGoalClientFactKind } from '../../__generated__/globalTypes';
import AccountPaneHeader from '../../components/headers/AccountPaneHeader';
import { useFeatureGates } from '../../hooks/useFeatureGates';
import { useRecordPageVisit } from '../../hooks/useRecordPageVisit';
import { isCreditCardAccount, isDepositoryAccount, isInvestmentAccount } from '../../utils/account/accounts';

import CreditAccountPane from './CreditAccountPane';
import DepositoryAccountPane from './DepositoryAccountPane';
import InvestmentAccountPane from './InvestmentAccountPane';

import type { GetFinancialAccountQuery, GetFinancialAccountQueryVariables } from './__generated__/AccountPane.gql';

const GET_FINANCIAL_ACCOUNT = gql`
  query GetFinancialAccount($id: ID!) {
    getFinancialAccount(id: $id) {
      id
      type
      currentBalance {
        id
        balance
        creditLimit
        timestamp
      }
      bank {
        id
        url
      }
      statementBill {
        id
        amountDue
        minAmountDue
        dueDate
        statementDate
        currencyCode
      }
      ...AccountPaneHeader
    }
  }
  ${AccountPaneHeader.Fragment}
`;

interface IAccountPaneProps {
  // Stories pass this directly so they don't need a router with a matching route
  // pattern. Production rendering from MainApp.tsx omits the prop and the
  // useParams() fallback kicks in.
  accountId?: string;
}

const AccountPane = ({ accountId: accountIdProp }: IAccountPaneProps = {}) => {
  const params = useParams();
  const accountId = accountIdProp ?? params.accountId ?? '';

  const { flags, loading: gatesLoading, resolved: gatesResolved } = useFeatureGates();

  const { data, loading, error } = useQuery<GetFinancialAccountQuery, GetFinancialAccountQueryVariables>(
    GET_FINANCIAL_ACCOUNT,
    {
      variables: { id: accountId },
    }
  );
  useRecordPageVisit(NuxGoalClientFactKind.PageVisitedAccount, data?.getFinancialAccount?.id);

  if (loading) {
    return <LoadingIndicator className="h-full" size="lg" fullScreen />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const financialAccount = data?.getFinancialAccount;

  if (!financialAccount) {
    return <div>Financial account not found</div>;
  }

  // Route to the appropriate component based on account type
  if (isCreditCardAccount(financialAccount)) {
    return <CreditAccountPane financialAccount={financialAccount} />;
  }

  if (isDepositoryAccount(financialAccount)) {
    return <DepositoryAccountPane financialAccount={financialAccount} />;
  }

  // The detail query is ungated, so a deep link or an entity chip lands here with the gate off.
  if (isInvestmentAccount(financialAccount)) {
    // Waits only on a read still in flight: a failed gate read fails closed to the fallback below.
    if (gatesLoading && !gatesResolved) {
      return <LoadingIndicator className="h-full" size="lg" fullScreen />;
    }
    if (flags.has(FeatureGate.InvestmentAccounts)) {
      return <InvestmentAccountPane financialAccount={financialAccount} />;
    }
  }

  return (
    <div className="flex h-full w-full flex-col gap-2 pt-6">
      <div className="flex h-full w-full flex-col gap-8 overflow-y-auto p-4">
        <div className="text-primary text-2xl">Account: {financialAccount.name}</div>
        <div className="text-secondary">
          Account type {financialAccount.type} - {financialAccount.subtype} is not yet supported
        </div>
      </div>
    </div>
  );
};

export default AccountPane;
